import { ChevronDown, Search, X } from 'lucide-react';
import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from './cn';
import { RoadSign } from './signs';

interface FieldWiring {
  id: string;
  describedBy?: string;
  invalid: boolean;
}

const FieldContext = createContext<FieldWiring | null>(null);

/** The control inside a <Field> picks up its id, aria-describedby and aria-invalid from here. */
export function useFieldWiring(id: string | undefined, invalid: boolean | undefined) {
  const field = useContext(FieldContext);
  return {
    id: id ?? field?.id,
    'aria-describedby': field?.describedBy,
    'aria-invalid': invalid ?? field?.invalid ?? undefined,
    invalid: Boolean(invalid ?? field?.invalid),
  };
}

export interface FieldProps {
  label: ReactNode;
  /** Help under the field; replaced by the error when there is one. */
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** The control's id, when it needs a fixed one. */
  id?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Label above the field — never floating inside it, so it stays legible in Arabic. An error is the
 * hazard octagon plus hazard ink, never colour alone.
 */
export function Field({ label, help, error, required, id, className, children }: FieldProps) {
  const auto = useId();
  const controlId = id ?? `f${auto}`;
  const noteId = `${controlId}-note`;
  const note = error || help;
  return (
    <FieldContext.Provider value={{ id: controlId, describedBy: note ? noteId : undefined, invalid: Boolean(error) }}>
      <div className={cn('grid min-w-0 content-start gap-1.5', className)}>
        <label htmlFor={controlId} className="text-body-sm font-bold text-ink">
          {label}
          {required && (
            <span className="ms-0.5 text-ink-subtle" aria-hidden="true">
              *
            </span>
          )}
        </label>
        {children}
        {error ? (
          <p id={noteId} className="flex items-center gap-1.5 text-body-sm text-hazard-ink">
            <RoadSign sign="hazard" size={14} />
            {error}
          </p>
        ) : help ? (
          <p id={noteId} className="text-body-sm text-ink-subtle">
            {help}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

const BOX =
  'flex h-control-md min-w-0 items-center gap-2 rounded-md border bg-surface px-3 text-body text-ink transition-[border-color,box-shadow] duration-quick ease-drive';
const BOX_STATE = (invalid: boolean) =>
  invalid
    ? 'border-hazard shadow-[0_0_0_1px_var(--vlm-hazard)]'
    : 'border-line-input focus-within:border-focus focus-within:shadow-[0_0_0_1px_var(--vlm-focus)]';
const BARE_INPUT =
  'h-full w-full min-w-0 flex-1 border-0 bg-transparent p-0 text-inherit outline-none placeholder:text-ink-subtle disabled:cursor-not-allowed disabled:text-ink-disabled';

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  leading?: ReactNode;
  trailing?: ReactNode;
  invalid?: boolean;
  /** Classes for the box around the input (width, height). */
  boxClassName?: string;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { leading, trailing, invalid, boxClassName, className, id, disabled, ...rest },
  ref,
) {
  const { invalid: isInvalid, ...wiring } = useFieldWiring(id, invalid);
  return (
    <div className={cn(BOX, BOX_STATE(isInvalid), disabled && 'bg-surface-sunken', boxClassName)}>
      {leading && <span className="flex shrink-0 text-ink-subtle">{leading}</span>}
      <input ref={ref} disabled={disabled} className={cn(BARE_INPUT, className)} {...wiring} {...rest} />
      {trailing && <span className="flex shrink-0 items-center text-ink-subtle">{trailing}</span>}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ invalid, className, id, rows = 3, ...rest }, ref) {
  const { invalid: isInvalid, ...wiring } = useFieldWiring(id, invalid);
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        'block w-full min-w-0 resize-y rounded-md border bg-surface px-3 py-2 text-body text-ink outline-none transition-[border-color,box-shadow] duration-quick ease-drive placeholder:text-ink-subtle disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-disabled',
        isInvalid ? 'border-hazard shadow-[0_0_0_1px_var(--vlm-hazard)]' : 'border-line-input focus:border-focus focus:shadow-[0_0_0_1px_var(--vlm-focus)]',
        className,
      )}
      {...wiring}
      {...rest}
    />
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  boxClassName?: string;
}

/** The native select, dressed: the platform's own list is the most accessible one in both directions. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ invalid, className, boxClassName, id, children, ...rest }, ref) {
  const { invalid: isInvalid, ...wiring } = useFieldWiring(id, invalid);
  return (
    <div className={cn('relative min-w-0', boxClassName)}>
      <select
        ref={ref}
        className={cn(
          'h-control-md w-full min-w-0 cursor-pointer appearance-none truncate rounded-md border bg-surface pe-9 ps-3 text-body text-ink outline-none transition-[border-color,box-shadow] duration-quick ease-drive disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-disabled',
          isInvalid ? 'border-hazard shadow-[0_0_0_1px_var(--vlm-hazard)]' : 'border-line-input focus:border-focus focus:shadow-[0_0_0_1px_var(--vlm-focus)]',
          className,
        )}
        {...wiring}
        {...rest}
      >
        {children}
      </select>
      <ChevronDown size={16} aria-hidden="true" className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
    </div>
  );
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox({ label, className, ...rest }, ref) {
  return (
    <label className={cn('inline-flex cursor-pointer items-center gap-2.5 text-body text-ink', className)}>
      <input ref={ref} type="checkbox" className="size-4 shrink-0 cursor-pointer accent-[var(--vlm-action)]" {...rest} />
      {label}
    </label>
  );
});

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Visible label; without one, pass aria-label. */
  label?: ReactNode;
}

/** On = action track with an on-action thumb (DESIGN_SYSTEM.md §7). A native checkbox with role="switch". */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch({ label, className, ...rest }, ref) {
  const input = <input ref={ref} type="checkbox" role="switch" className="vlm-switch" {...rest} />;
  if (!label) return input;
  return (
    <label className={cn('inline-flex cursor-pointer items-center gap-2.5 text-body text-ink has-[:disabled]:cursor-not-allowed has-[:disabled]:text-ink-disabled', className)}>
      {input}
      {label}
    </label>
  );
});

export interface SearchInputProps extends Omit<TextInputProps, 'type' | 'leading' | 'onChange' | 'value'> {
  value: string;
  onValueChange: (value: string) => void;
  clearLabel: string;
  /** A key hint ("/"), shown while the field is empty. */
  shortcut?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value, onValueChange, clearLabel, shortcut, ...rest },
  ref,
) {
  return (
    <TextInput
      ref={ref}
      type="search"
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && value) {
          event.stopPropagation();
          onValueChange('');
        }
      }}
      leading={<Search size={16} aria-hidden="true" />}
      trailing={
        value ? (
          <button
            type="button"
            aria-label={clearLabel}
            onClick={() => onValueChange('')}
            className="grid size-6 place-items-center rounded-sm text-ink-subtle hover:bg-hover hover:text-ink"
          >
            <X size={14} aria-hidden="true" />
          </button>
        ) : shortcut ? (
          <kbd className="rounded-xs border border-line-strong px-[5px] py-[3px] font-mono text-[11px] font-semibold leading-none text-ink-subtle">{shortcut}</kbd>
        ) : null
      }
      className="[&::-webkit-search-cancel-button]:hidden"
      {...rest}
    />
  );
});
